import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

// Types
export type MentionEligibilityReason = 'not_in_group' | 'no_giveaway' | 'already_rewarded' | null;

export interface MentionGiveawaySummary {
  id: number;
  name: string;
  startsOn: string;
  endsOn: string;
  mentionUntil: string;
  status: 'upcoming' | 'open' | 'grace' | 'closed';
}

export interface MentionEligibility {
  status: 'blocked' | 'allowed';
  reason: MentionEligibilityReason;
  giveaway: MentionGiveawaySummary | null;
  lastMentionId: number | null;
  lastGrantedAt: string | null;
}

export interface Student {
  id: number;
  firstName: string;
  lastName: string;
  surname?: string;
  contacts?: string;
  startDate: string;
  endDate?: string;
  quitDate?: string;
  subExpEstimate?: string;
  /** Churn-risk flag set by the teacher; cleared automatically on a new subscription payment */
  atRisk?: boolean;
  riskLevel?: 'moderate' | 'certain';
  riskMarkedAt?: string;
  riskPotentialEndDate?: string;
  riskNote?: string;
  groups?: Array<{
    id: number;
    name?: string;
    level: string;
    /** Date the student joined this group (group_students.attributed_on) */
    attributedOn?: string | null;
  }>;
  /** Individual public-offer (contract) number — rare, mostly legacy; assigned manually */
  individualOfferRef?: { id: number; number: number; date: string } | null;
  referredByStudentId?: number | null;
  referrer?: {
    id: number;
    firstName?: string | null;
    lastName?: string | null;
    surname?: string | null;
    quitDate?: string | null;
  } | null;
  /** Referrer recorded and the friend has paid — the gift can be confirmed now */
  pendingReferralReward?: boolean;
  /** Referrer recorded, but the friend has not paid their first month yet */
  referralRewardAwaitingPayment?: boolean;
  referralReward?: StudentSubscription | null;
  mentionEligibility?: MentionEligibility;
  createdAt: string;
  updatedAt: string;
}

export interface StudentFormData {
  firstName: string;
  lastName: string;
  surname?: string;
  groupId?: number;
  contacts?: string;
  startDate: string;
  quitDate?: string | null;
  referredByStudentId?: number | null;
}

export interface VacationTypeItem {
  id: number;
  name: string;
  isIllness: boolean;
  isFreeze?: boolean;
}

export interface StudentVacation {
  id: number;
  studentId: number;
  vacationTypeId: number;
  vacationType?: VacationTypeItem;
  dateFrom: string;
  dateTo: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Student vacation row from GET /api/student-vacations (includes nested student) */
export interface StudentVacationRow extends StudentVacation {
  student?: Pick<Student, 'id' | 'firstName' | 'lastName' | 'surname'>;
}

export interface SubscriptionTypeItem {
  id: number;
  name: string;
  pricePerLesson: string;
  defaultLessons: number;
  isFreeze?: boolean;
  isReward?: boolean;
  rewardKind?: 'referral' | 'mention' | null;
}

/** Live freeze-allowance snapshot from GET /freezes/allowance */
export interface FreezeAllowance {
  usedDays: number;
  remainingDays: number;
  maxDays: number;
  year: number;
  price: number | null;
  computedTo: string | null;
}

/** Freeze preset: whole-month durations or a hand-picked custom range */
export type FreezePreset = '1m' | '2m' | 'custom';

export interface StudentSubscription {
  id: number;
  studentId: number;
  subscriptionTypeId: number;
  startDate: string;
  lessonsCount: number;
  pricePerLesson: string;
  paymentDate: string;
  revenue?: string;
  /** Manual end date (optional) */
  endDate?: string | null;
  /** Persisted schedule-based estimate when endDate is null */
  estimatedEndDate?: string | null;
  /** Resolved end for display: endDate ?? estimatedEndDate ?? live compute */
  effectiveEndDate?: string | null;
  totalAmount?: string;
  subscriptionType?: SubscriptionTypeItem;
  proofUrl?: string | null;
  proofObjectKey?: string | null;
  referredStudentId?: number | null;
  giveawayId?: number | null;
  giveaway?: {
    id: number;
    name: string;
    startsOn?: string;
    endsOn?: string;
  } | null;
  referredStudent?: {
    id: number;
    firstName?: string | null;
    lastName?: string | null;
    surname?: string | null;
  } | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface StudentsState {
  students: Student[];
  currentStudent: Student | null;
  studentVacations: StudentVacation[];
  /** All teacher-visible student vacations (table page) */
  allStudentVacations: StudentVacationRow[];
  vacationTypes: VacationTypeItem[];
  loadingVacations: boolean;
  loadingAllVacations: boolean;
  studentSubscriptions: StudentSubscription[];
  subscriptionTypes: SubscriptionTypeItem[];
  loadingSubscriptions: boolean;
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
  filters: {
    search: string;
    groupId?: number;
    level?: string;
    status?: 'active' | 'quitted';
  };
}

// Initial state
const initialState: StudentsState = {
  students: [],
  currentStudent: null,
  studentVacations: [],
  allStudentVacations: [],
  vacationTypes: [],
  loadingVacations: false,
  loadingAllVacations: false,
  studentSubscriptions: [],
  subscriptionTypes: [],
  loadingSubscriptions: false,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
  },
  filters: {
    search: '',
    groupId: undefined,
    level: undefined,
    // Default to active students; clearing filters returns here too.
    status: 'active',
  },
};

// API Base URL
const API_BASE_URL = import.meta.env.VITE_API_URL as string;

// Helper function to get auth headers
const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Async thunks
export const fetchStudentsAsync = createAsyncThunk(
  'students/fetchStudents',
  async (params: { page?: number; limit?: number; search?: string; groupId?: number; level?: string; status?: 'active' | 'quitted' } = {}, { rejectWithValue, getState }) => {
    try {
      const { page = 1, limit = 50, search = '', groupId, level, status } = params;
      
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      
      if (search) queryParams.append('search', search);
      if (groupId) queryParams.append('groupId', groupId.toString());
      if (level) queryParams.append('level', level);
      if (status) queryParams.append('status', status);

      const response = await axios.get(`${API_BASE_URL}/students?${queryParams}`, {
        headers: getAuthHeaders(getState)
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch students'));
    }
  }
);

export const createStudentAsync = createAsyncThunk(
  'students/createStudent',
  async (studentData: StudentFormData, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/students`, studentData, {
        headers: getAuthHeaders(getState)
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create student'));
    }
  }
);

export const updateStudentAsync = createAsyncThunk(
  'students/updateStudent',
  async ({ id, studentData }: { id: number; studentData: Partial<StudentFormData> }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/students/${id}`, studentData, {
        headers: getAuthHeaders(getState)
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update student'));
    }
  }
);

export const deleteStudentAsync = createAsyncThunk(
  'students/deleteStudent',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      await axios.delete(`${API_BASE_URL}/students/${id}`, {
        headers: getAuthHeaders(getState)
      });

      return { id };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete student'));
    }
  }
);

export const fetchStudentByIdAsync = createAsyncThunk(
  'students/fetchStudentById',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/students/${id}`, {
        headers: getAuthHeaders(getState)
      });

      return response.data.student;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch student'));
    }
  }
);

export const assignIndividualOfferAsync = createAsyncThunk(
  'students/assignIndividualOffer',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/students/${id}/individual-offer`, {}, {
        headers: getAuthHeaders(getState)
      });

      return response.data.offer as { id: number; number: number; date: string };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to assign individual offer'));
    }
  }
);

export const bulkCreateStudentsAsync = createAsyncThunk(
  'students/bulkCreateStudents',
  async ({ students, groupId }: { students: StudentFormData[]; groupId?: number }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/students/bulk`, { students, groupId }, {
        headers: getAuthHeaders(getState)
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create students'));
    }
  }
);

// Student Vacation thunks
export const fetchVacationTypesAsync = createAsyncThunk(
  'students/fetchVacationTypes',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/vacations/types`, {
        headers: getAuthHeaders(getState)
      });
      return response.data.types as VacationTypeItem[];
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch vacation types'));
    }
  }
);

export const fetchStudentVacationsAsync = createAsyncThunk(
  'students/fetchStudentVacations',
  async (studentId: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/students/${studentId}/vacations`, {
        headers: getAuthHeaders(getState)
      });
      return response.data.vacations as StudentVacation[];
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch student vacations'));
    }
  }
);

export const fetchAllStudentVacationsAsync = createAsyncThunk(
  'students/fetchAllStudentVacations',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/student-vacations`, {
        headers: getAuthHeaders(getState)
      });
      return response.data.vacations as StudentVacationRow[];
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch student vacations'));
    }
  }
);

export const createStudentVacationAsync = createAsyncThunk(
  'students/createStudentVacation',
  async ({ studentId, data }: { studentId: number; data: { vacationTypeId: number; dateFrom: string; dateTo: string } }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/students/${studentId}/vacations`, data, {
        headers: getAuthHeaders(getState)
      });
      return response.data.vacation as StudentVacation;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create vacation'));
    }
  }
);

export const updateStudentVacationAsync = createAsyncThunk(
  'students/updateStudentVacation',
  async ({ studentId, vacationId, data }: { studentId: number; vacationId: number; data: { vacationTypeId?: number; dateFrom?: string; dateTo?: string } }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/students/${studentId}/vacations/${vacationId}`, data, {
        headers: getAuthHeaders(getState)
      });
      return response.data.vacation as StudentVacation;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update vacation'));
    }
  }
);

export const deleteStudentVacationAsync = createAsyncThunk(
  'students/deleteStudentVacation',
  async ({ studentId, vacationId }: { studentId: number; vacationId: number }, { rejectWithValue, getState }) => {
    try {
      await axios.delete(`${API_BASE_URL}/students/${studentId}/vacations/${vacationId}`, {
        headers: getAuthHeaders(getState)
      });
      return vacationId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete vacation'));
    }
  }
);

// Subscription thunks
export const fetchSubscriptionTypesAsync = createAsyncThunk(
  'students/fetchSubscriptionTypes',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/subscription-types`, {
        headers: getAuthHeaders(getState)
      });
      return response.data.types as SubscriptionTypeItem[];
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch subscription types'));
    }
  }
);

export const fetchStudentSubscriptionsAsync = createAsyncThunk(
  'students/fetchStudentSubscriptions',
  async (studentId: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/students/${studentId}/subscriptions`, {
        headers: getAuthHeaders(getState)
      });
      return response.data.subscriptions as StudentSubscription[];
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch subscriptions'));
    }
  }
);

export const createStudentSubscriptionAsync = createAsyncThunk(
  'students/createStudentSubscription',
  async ({ studentId, data }: { studentId: number; data: { subscriptionTypeId: number; startDate: string; lessonsCount: number; totalAmount: number; paymentDate: string; endDate?: string | null } }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/students/${studentId}/subscriptions`, data, {
        headers: getAuthHeaders(getState)
      });
      return response.data.subscription as StudentSubscription;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create subscription'));
    }
  }
);

export const updateStudentSubscriptionAsync = createAsyncThunk(
  'students/updateStudentSubscription',
  async ({ studentId, subscriptionId, data }: { studentId: number; subscriptionId: number; data: { startDate?: string; lessonsCount?: number; totalAmount?: number; paymentDate?: string; endDate?: string | null } }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/students/${studentId}/subscriptions/${subscriptionId}`, data, {
        headers: getAuthHeaders(getState)
      });
      return response.data.subscription as StudentSubscription;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update subscription'));
    }
  }
);

export const deleteStudentSubscriptionAsync = createAsyncThunk(
  'students/deleteStudentSubscription',
  async ({ studentId, subscriptionId }: { studentId: number; subscriptionId: number }, { rejectWithValue, getState }) => {
    try {
      await axios.delete(`${API_BASE_URL}/students/${studentId}/subscriptions/${subscriptionId}`, {
        headers: getAuthHeaders(getState)
      });
      return subscriptionId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete subscription'));
    }
  }
);

// Freeze ("paid vacation") thunks — a freeze is a real subscription, so create/update/delete
// reuse the studentSubscriptions list in state.
export const fetchFreezeAllowanceAsync = createAsyncThunk(
  'students/fetchFreezeAllowance',
  async (
    { studentId, from, preset, to, excludeSubId }:
      { studentId: number; from?: string; preset?: FreezePreset; to?: string; excludeSubId?: number },
    { rejectWithValue, getState }
  ) => {
    try {
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      if (preset && preset !== 'custom') params.append('preset', preset);
      if (excludeSubId) params.append('excludeSubId', excludeSubId.toString());
      const response = await axios.get(
        `${API_BASE_URL}/students/${studentId}/freezes/allowance?${params}`,
        { headers: getAuthHeaders(getState) }
      );
      return response.data.allowance as FreezeAllowance;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch freeze allowance'));
    }
  }
);

export const createFreezeAsync = createAsyncThunk(
  'students/createFreeze',
  async (
    { studentId, data }:
      { studentId: number; data: { from: string; preset?: FreezePreset; to?: string } },
    { rejectWithValue, getState }
  ) => {
    try {
      const body: Record<string, unknown> = { from: data.from };
      if (data.preset && data.preset !== 'custom') body.preset = data.preset;
      else if (data.to) body.to = data.to;
      const response = await axios.post(`${API_BASE_URL}/students/${studentId}/freezes`, body, {
        headers: getAuthHeaders(getState)
      });
      return response.data.subscription as StudentSubscription;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create freeze'));
    }
  }
);

export const updateFreezeAsync = createAsyncThunk(
  'students/updateFreeze',
  async (
    { studentId, subscriptionId, data }:
      { studentId: number; subscriptionId: number; data: { from: string; preset?: FreezePreset; to?: string } },
    { rejectWithValue, getState }
  ) => {
    try {
      const body: Record<string, unknown> = { from: data.from };
      if (data.preset && data.preset !== 'custom') body.preset = data.preset;
      else if (data.to) body.to = data.to;
      const response = await axios.put(
        `${API_BASE_URL}/students/${studentId}/freezes/${subscriptionId}`,
        body,
        { headers: getAuthHeaders(getState) }
      );
      return response.data.subscription as StudentSubscription;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update freeze'));
    }
  }
);

export const deleteFreezeAsync = createAsyncThunk(
  'students/deleteFreeze',
  async ({ studentId, subscriptionId }: { studentId: number; subscriptionId: number }, { rejectWithValue, getState }) => {
    try {
      await axios.delete(`${API_BASE_URL}/students/${studentId}/freezes/${subscriptionId}`, {
        headers: getAuthHeaders(getState)
      });
      return subscriptionId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete freeze'));
    }
  }
);

export const fetchSuggestedStartDateAsync = createAsyncThunk(
  'students/fetchSuggestedStartDate',
  async ({ studentId }: { studentId: number }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/students/${studentId}/subscriptions/suggest-start`, {
        headers: getAuthHeaders(getState)
      });
      return response.data.suggestedStartDate as string | null;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch suggested start date'));
    }
  }
);

export interface ComputeEndDateResult {
  estimatedEndDate: string | null;
  /** Exact lesson dates the subscription period covers on the group schedule */
  coveredDates: string[];
  coveredCount: number;
}

export const computeEndDateAsync = createAsyncThunk(
  'students/computeEndDate',
  async (
    data: {
      studentId: number;
      startDate: string;
      lessonsCount: number;
      /** Optional preview vacation not yet saved — merged into schedule computation */
      previewStudentVacation?: { dateFrom: string; dateTo: string };
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/subscriptions/compute-end-date`, data, {
        headers: getAuthHeaders(getState)
      });
      return {
        estimatedEndDate: (response.data.estimatedEndDate ?? null) as string | null,
        coveredDates: (response.data.coveredDates ?? []) as string[],
        coveredCount: (response.data.coveredCount ?? 0) as number,
      } satisfies ComputeEndDateResult;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to compute end date'));
    }
  }
);

/** Patch only the end date of a subscription (null = revert to schedule-based estimate) */
export const patchSubscriptionEndDateAsync = createAsyncThunk(
  'students/patchSubscriptionEndDate',
  async (
    { studentId, subscriptionId, endDate }: { studentId: number; subscriptionId: number; endDate: string | null },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/students/${studentId}/subscriptions/${subscriptionId}`,
        { endDate },
        { headers: getAuthHeaders(getState) }
      );
      return response.data.subscription as StudentSubscription;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update subscription end date'));
    }
  }
);

/** Move a student to another group, effective `movedOn` (defaults server-side to today). */
export const moveStudentGroupAsync = createAsyncThunk(
  'students/moveStudentGroup',
  async (
    { studentId, toGroupId, movedOn }: { studentId: number; toGroupId: number; movedOn?: string },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/students/${studentId}/move-group`,
        { toGroupId, ...(movedOn ? { movedOn } : {}) },
        { headers: getAuthHeaders(getState) }
      );
      return response.data.student as Student;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to move student'));
    }
  }
);

/** Flag a student as at risk of leaving (or update the level/date/note of an existing flag). */
export const flagStudentRiskAsync = createAsyncThunk(
  'students/flagStudentRisk',
  async (
    { id, riskLevel, potentialEndDate, note }:
      { id: number; riskLevel: 'moderate' | 'certain'; potentialEndDate?: string | null; note?: string | null },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/students/${id}/risk`,
        { riskLevel, potentialEndDate, note },
        { headers: getAuthHeaders(getState) }
      );
      return response.data.student as Student;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to flag student at risk'));
    }
  }
);

/** Clear a student's at-risk flag (history is preserved server-side). */
export const clearStudentRiskAsync = createAsyncThunk(
  'students/clearStudentRisk',
  async ({ id, note }: { id: number; note?: string | null }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/students/${id}/risk`, {
        headers: getAuthHeaders(getState),
        data: { note }
      });
      return response.data.student as Student;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to clear student risk'));
    }
  }
);

// Slice
const studentsSlice = createSlice({
  name: 'students',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<Partial<StudentsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearCurrentStudent: (state) => {
      state.currentStudent = null;
      state.studentVacations = [];
      state.studentSubscriptions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch students
      .addCase(fetchStudentsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudentsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.students = action.payload.students;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchStudentsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch student by ID
      .addCase(fetchStudentByIdAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudentByIdAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.currentStudent = action.payload;
        state.error = null;
      })
      .addCase(fetchStudentByIdAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Assign individual offer
      .addCase(assignIndividualOfferAsync.fulfilled, (state, action) => {
        if (state.currentStudent) {
          state.currentStudent.individualOfferRef = action.payload;
        }
      })
      .addCase(assignIndividualOfferAsync.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Create student
      .addCase(createStudentAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStudentAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.students.unshift(action.payload.student);
      })
      .addCase(createStudentAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update student
      .addCase(updateStudentAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStudentAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.students.findIndex(s => s.id === action.payload.student.id);
        if (index !== -1) {
          state.students[index] = action.payload.student;
        }
      })
      .addCase(updateStudentAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete student
      .addCase(deleteStudentAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStudentAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.students = state.students.filter(s => s.id !== action.payload.id);
      })
      .addCase(deleteStudentAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Bulk create students
      .addCase(bulkCreateStudentsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkCreateStudentsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.students.unshift(...action.payload.students);
      })
      .addCase(bulkCreateStudentsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Vacation types
      .addCase(fetchVacationTypesAsync.fulfilled, (state, action: PayloadAction<VacationTypeItem[]>) => {
        state.vacationTypes = action.payload;
      })
      // Student vacations
      .addCase(fetchStudentVacationsAsync.pending, (state) => {
        state.loadingVacations = true;
      })
      .addCase(fetchStudentVacationsAsync.fulfilled, (state, action: PayloadAction<StudentVacation[]>) => {
        state.loadingVacations = false;
        state.studentVacations = action.payload;
      })
      .addCase(fetchStudentVacationsAsync.rejected, (state, action) => {
        state.loadingVacations = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAllStudentVacationsAsync.pending, (state) => {
        state.loadingAllVacations = true;
        state.error = null;
      })
      .addCase(fetchAllStudentVacationsAsync.fulfilled, (state, action: PayloadAction<StudentVacationRow[]>) => {
        state.loadingAllVacations = false;
        state.allStudentVacations = action.payload;
      })
      .addCase(fetchAllStudentVacationsAsync.rejected, (state, action) => {
        state.loadingAllVacations = false;
        state.error = action.payload as string;
      })
      .addCase(createStudentVacationAsync.fulfilled, (state, action: PayloadAction<StudentVacation>) => {
        state.studentVacations.unshift(action.payload);
      })
      .addCase(createStudentVacationAsync.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(updateStudentVacationAsync.fulfilled, (state, action: PayloadAction<StudentVacation>) => {
        const index = state.studentVacations.findIndex(v => v.id === action.payload.id);
        if (index !== -1) {
          state.studentVacations[index] = action.payload;
        }
        const idxAll = state.allStudentVacations.findIndex(v => v.id === action.payload.id);
        if (idxAll !== -1) {
          const prev = state.allStudentVacations[idxAll];
          state.allStudentVacations[idxAll] = { ...prev, ...action.payload, student: prev.student };
        }
      })
      .addCase(updateStudentVacationAsync.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(deleteStudentVacationAsync.fulfilled, (state, action: PayloadAction<number>) => {
        state.studentVacations = state.studentVacations.filter(v => v.id !== action.payload);
        state.allStudentVacations = state.allStudentVacations.filter(v => v.id !== action.payload);
      })
      .addCase(deleteStudentVacationAsync.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Subscription types
      .addCase(fetchSubscriptionTypesAsync.fulfilled, (state, action: PayloadAction<SubscriptionTypeItem[]>) => {
        state.subscriptionTypes = action.payload;
      })
      // Student subscriptions
      .addCase(fetchStudentSubscriptionsAsync.pending, (state) => {
        state.loadingSubscriptions = true;
      })
      .addCase(fetchStudentSubscriptionsAsync.fulfilled, (state, action: PayloadAction<StudentSubscription[]>) => {
        state.loadingSubscriptions = false;
        state.studentSubscriptions = action.payload;
      })
      .addCase(fetchStudentSubscriptionsAsync.rejected, (state, action) => {
        state.loadingSubscriptions = false;
        state.error = action.payload as string;
      })
      .addCase(createStudentSubscriptionAsync.fulfilled, (state, action: PayloadAction<StudentSubscription>) => {
        state.studentSubscriptions.unshift(action.payload);
      })
      .addCase(createStudentSubscriptionAsync.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(updateStudentSubscriptionAsync.fulfilled, (state, action: PayloadAction<StudentSubscription>) => {
        const index = state.studentSubscriptions.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.studentSubscriptions[index] = action.payload;
        }
      })
      .addCase(updateStudentSubscriptionAsync.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(deleteStudentSubscriptionAsync.fulfilled, (state, action: PayloadAction<number>) => {
        state.studentSubscriptions = state.studentSubscriptions.filter(s => s.id !== action.payload);
      })
      .addCase(deleteStudentSubscriptionAsync.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Freezes (real subscriptions, kept in the same list)
      .addCase(createFreezeAsync.fulfilled, (state, action: PayloadAction<StudentSubscription>) => {
        state.studentSubscriptions.unshift(action.payload);
      })
      .addCase(updateFreezeAsync.fulfilled, (state, action: PayloadAction<StudentSubscription>) => {
        const index = state.studentSubscriptions.findIndex(s => s.id === action.payload.id);
        if (index !== -1) state.studentSubscriptions[index] = action.payload;
      })
      .addCase(deleteFreezeAsync.fulfilled, (state, action: PayloadAction<number>) => {
        state.studentSubscriptions = state.studentSubscriptions.filter(s => s.id !== action.payload);
      })
      .addCase(patchSubscriptionEndDateAsync.fulfilled, (state, action: PayloadAction<StudentSubscription>) => {
        const index = state.studentSubscriptions.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.studentSubscriptions[index] = action.payload;
        }
      })
      .addCase(patchSubscriptionEndDateAsync.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Risk flag/clear — both return the updated student; sync currentStudent + list row
      .addCase(flagStudentRiskAsync.fulfilled, (state, action: PayloadAction<Student>) => {
        if (state.currentStudent && state.currentStudent.id === action.payload.id) {
          state.currentStudent = { ...state.currentStudent, ...action.payload };
        }
        const index = state.students.findIndex(s => s.id === action.payload.id);
        if (index !== -1) state.students[index] = { ...state.students[index], ...action.payload };
      })
      .addCase(flagStudentRiskAsync.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(clearStudentRiskAsync.fulfilled, (state, action: PayloadAction<Student>) => {
        if (state.currentStudent && state.currentStudent.id === action.payload.id) {
          state.currentStudent = { ...state.currentStudent, ...action.payload };
        }
        const index = state.students.findIndex(s => s.id === action.payload.id);
        if (index !== -1) state.students[index] = { ...state.students[index], ...action.payload };
      })
      .addCase(clearStudentRiskAsync.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setFilters, clearFilters, clearCurrentStudent } = studentsSlice.actions;
export default studentsSlice.reducer; 